// Dashboard (spec §6.7) — Home.
//
// Two layouts over the same queries:
//
// Below lg (Mini App):
//   1. The stat strip (Trades today · Volume · Margin · Last trade) as a 2×2
//   2. Operations (entry tiles)
//   3. Needs attention (open cheques, settle-later parties, unaccounted chain)
//   4. Recent activity (the journal, as cards)
//   5. Treasury (the five moving accounts)
//
// lg+ (desktop console):
//   1. Terminal strip (summary metrics band)
//   2. Needs attention (two thirds) · Treasury (one third)
//   3. Recent activity (the journal, full width)
//
// 21 Oct 2026 — WHAT HE ACTS ON COMES FIRST (operator, overnight: "I need clean
// view for operator"). Needs attention stood LAST on the phone, under twelve
// journal cards, and in the desktop's lower row, under the whole journal — a
// settle-later trade or an unaccounted transfer was a scroll away on the one
// screen that exists to show it. It is now ONE list on both layouts, directly
// under the strip (and the phone's tiles): open cheques, settle-later parties
// and unaccounted chain transfers, each row naming its own kind, so the list has
// no headings; the desktop's Open cheques strip cell and Chain card, and the
// phone's separate Open cheques block, said the same facts a second time and are
// gone. Nothing open is ONE muted line, not a card.
//
// 14 Sept 2026 (SWEEP-SCOPE §5.21): one name per card and feed on both
// layouts ("Recent trades", "Open cheques", "Recent money", "Treasury"), the
// words carry a trade's direction (no Buy/Sell pill under a heading that does
// not say Customer, no colour by direction), empty states are five words,
// the grid holds its four cards on ONE row so "Needs attention" — the card
// this screen exists for — never wraps alone, and a trade row on either
// layout opens the deal panel the trades log uses. The retired mirror card,
// the unused QuickAction and its icon imports are gone.
//
// 17 Sept 2026 (operator, on the desktop grid: "see the recent trade screen —
// so dirty and unorganised"): at lg+ Recent trades is the TRADES LOG'S OWN
// TABLE — When · Type · Party · Pays · Receives · Rate · Status on the Ledger
// seats, one line per deal — across the whole width, with Chain, Treasury and
// Needs attention on the row beneath it. Two-line prose rows in a quarter-width
// card were what he was looking at. The phone keeps Ledger's DealRowLine.
//
// 18 Sept 2026 (operator: "NEED RECENT MONEY MOVEMENTS HERE AS WELL"): at lg+ a full-width Recent money card under Recent trades — components/MovementTable over the Dubai desk's ten newest movements (the Money route), Account and Balance on, no reverse door; the phone's Recent money list is unchanged.
//
// 19 Sept 2026 (operator: "fix the phone layout for the home screen too"):
// the phone now has what the desktop has, in the phone's order — the stat
// strip as a 2×2 card above the tiles, then Open cheques, Recent trades on
// DealRowLine's two-line form (rate at line 1's end, legs on line 2), Recent
// money as MovementTable's phone cards over the SAME ten movements the
// desktop card reads (the old In/Out flow list and its recent-flows read are
// gone), Treasury as the desktop's five moving accounts with "More accounts
// (N) →", and Needs attention as the settle-later rows or the desktop's
// quiet line. The summary, treasury and movements reads are no longer
// gated on useIsDesktop — the phone needs them for the strip and the two
// new feeds; same routes, same payloads. The desktop grid is untouched.
//
// 24 Sept 2026 — RECENT TRADES AND RECENT MONEY BECAME ONE JOURNAL (operator,
// 21 Sept, on this screen: "we are having entries multiple times and repeating
// patterns bcoz trades cause money movements as well", asking whether the two
// could be one table; ruled the same day "worth it — but not everywhere" —
// the party page and Home, leaving Money movements, the bank-statement view
// for reconciling, and the Trades log, the deal register, alone. The party
// page shipped 22 Sept; this is Home's half). A spot trade was three rows
// between the two cards — once under Recent trades, then once in each of its
// two currency blocks under Recent money — which is the repetition he was
// reading. components/MovementTable's MovementJournal stands every
// transaction ONCE, its currencies as lines inside its own row, on the party
// journal's own Movement · Balance seats. One read, not two: the movements
// route already carries trades (MovementKind 'trade') and money together, so
// the deals read stays only for the Last trade cell and the deal panel. What
// the removed Recent trades table alone carried — the deal's rate and its
// lifecycle chip — is now in the deal panel the Reference opens, and open
// settle-later deals keep their own card in Needs attention below.
//
// THE DEPTH AND WHAT IS NOT ON THE ROW are the things to reconsider first if
// he asks for a change here: twelve transactions, and no Rate column (on a
// feed that is mostly money entries it would stand blank on most rows).

import { Fragment, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Pending } from '../components/Pending';
import { Card, EmptyState, Heading, StateLine } from '../components/kit';
import { ReadFailed } from '../components/OnboardingParts';
import { ActionBar } from '../components/ActionBar';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, type ChainRow as ChainTransfer, type DealRow, type TreasuryRow, type SettlementParty, type TradeRow } from '../lib/api';
import { displayAsset, fmtAmount, fmtRelative, collapseTotals } from '../lib/format';
import { railName, partyName, partitionByRelevance, partyBoxWords } from '../lib/naming';
import { Name } from '../components/Name';
import { DealSlideOver } from '../components/DealSlideOver';
import { MovementJournal, withAbsentDeals } from '../components/MovementTable';
import { isWebContext } from '../lib/webSession';

type PendingMc = Awaited<ReturnType<typeof api.pendingMcs>>['pending'][number];

/** RECENT ACTIVITY DEPTH — the newest transactions on the Dubai desk, trades
 *  and money in one feed (the Worker's movements route carries both). Twelve,
 *  not the ten this card read while it was Recent money alone: it now stands
 *  where two cards stood, and a busy morning's money would otherwise push the
 *  morning's trades off a glance that is supposed to hold both. */
const ACTIVITY_ROWS = 12;

/** Treasury glance depth. Five is what fits beside Recent trades without
 *  the card growing its own scrollbar; everything else is one click away. */
const GLANCE_RAILS = 5;

/** Unaccounted-for transfers named in Needs attention. Asked for AND sliced:
 *  the row count is the list's promise, not the endpoint's — the rest is one
 *  line, "N more unaccounted for →". */
const CHAIN_GLANCE_ROWS = 3;

/** THE DEAL PANEL NEEDS THE TRADES LOG'S ROW SHAPE. There is no by-id read for
 *  a deal, so Home asks the trades log's route for a modest page — the ten
 *  recent deals on this screen are always inside the newest fifty — under the
 *  log's key shape, keyed by page size. A recent deal not on that page still
 *  opens: the glance row carries every field the panel renders except the
 *  delivery method, which a fallback row leaves null. (Phase C review, 14 Sept
 *  2026: fifty, not the log's two hundred — Home is a glance, not the log.) */
const TRADE_PAGE = 50;

/** A recent deal in the trades log's row shape — for the deal panel, and for
 *  the journal line of a trade the movements feed never carried. Everything
 *  the panel renders is on a glance row except the delivery method. */
function asTradeRow(d: DealRow): TradeRow {
  return {
    deal_id: d.deal_id,
    direction: d.direction,
    asset_take: d.asset_take,
    asset_give: d.asset_give,
    amount_take: d.amount_take,
    amount_give: d.amount_give,
    rate: d.rate,
    spread_amount: d.spread_amount,
    payment_method: d.payment_method,
    delivery_method: null,
    booked_at: d.booked_at,
    booked_by_admin: d.booked_by_admin,
    party_slug: d.party_slug ?? '',
    party_name: d.party_name ?? '',
    lifecycle: d.lifecycle,
  };
}

export function Dashboard() {
  const me    = useQuery({ queryKey: ['me'], queryFn: api.me });
  // AS DEEP AS THE JOURNAL, not ten (Astra review, 24 Sept 2026): Recent
  // activity merges this read with the movements feed and keeps the newest
  // ACTIVITY_ROWS of the two. Asking for fewer deals than the card can show
  // means a morning of nothing but settle-later trades — which move no
  // account and so reach the card through this read alone — would be cut at
  // ten and padded with older movements. The depth is IN the key, so the two
  // cannot drift apart.
  const deals = useQuery({
    queryKey: ['deals', 'recent', ACTIVITY_ROWS],
    queryFn:  () => api.recentDeals('dubaiotc', ACTIVITY_ROWS),
  });
  // The stat strip on both layouts (19 Sept 2026).
  const summary = useQuery({ queryKey: ['summary'], queryFn: api.summary });
  const mcs     = useQuery({ queryKey: ['mc', 'pending'], queryFn: () => api.pendingMcs() });
  // Settle-later trades still open. Reading this also runs the sweep, so
  // opening Home is what keeps the list honest (operator, 2 Sept: "need some
  // mechanism to do that often so that it stays visible").
  const settle  = useQuery({ queryKey: ['settlements'], queryFn: () => api.settlements(), refetchOnWindowFocus: true, refetchInterval: 30_000 });
  // On-chain transfers the books have no entry for. The one number on this page
  // that is not self-reported: a balance check can say the wallet disagrees, only
  // this can say WHICH transfers are unaccounted for.
  // THE LIMIT IS PART OF THE KEY. The Chain screen reads the same status with no
  // limit (100 rows) under ['chain', tab]; sharing ['chain','pending'] meant
  // whichever screen fetched last owned the cache entry, so opening Chain and
  // coming back to Home inside the 30-second staleTime put a hundred-row page
  // in a glance card. Prefix invalidation on ['chain'] still covers both.
  const chainQ  = useQuery({
    queryKey: ['chain', 'pending', CHAIN_GLANCE_ROWS],
    queryFn:  () => api.chainActivity('pending', CHAIN_GLANCE_ROWS),
  });
  // Treasury glance on both layouts (19 Sept 2026); key matches the
  // Treasury tab so the cache is shared.
  const treasury = useQuery({
    queryKey: ['treasury', 'balances'],
    queryFn:  () => api.treasuryBalances(),
  });
  // Recent money (18 Sept 2026): the Money screen's route, the newest ten —
  // the desktop's table and, since 19 Sept, the phone's cards, one read.
  const money = useQuery({
    queryKey: ['movements', 'home', ACTIVITY_ROWS],
    queryFn:  () => api.movements({ entity: 'dubaiotc', limit: ACTIVITY_ROWS }),
    refetchOnWindowFocus: true,
  });
  const recentDeals = deals.data?.deals ?? [];

  // The deal panel (14 Sept 2026): the trades log's page, same key, read only
  // once a recent trade is on screen.
  const [selected, setSelected] = useState<TradeRow | null>(null);
  const trades = useQuery({
    queryKey: ['trades', 'log', 'dubaiotc', TRADE_PAGE],
    queryFn:  () => api.listTrades('dubaiotc', TRADE_PAGE),
    enabled:  recentDeals.length > 0,
    staleTime: 60_000,
  });
  const tradesById = useMemo(() => {
    const byId = new Map<string, TradeRow>((trades.data?.trades ?? []).map((t) => [t.deal_id, t]));
    // A recent deal the log's page does not reach is still on this screen, so
    // it must still open its panel AND still draw its own legs in the journal.
    // The glance row carries every field the panel renders except the delivery
    // method, which asTradeRow leaves null.
    for (const d of recentDeals) if (!byId.has(d.deal_id)) byId.set(d.deal_id, asTradeRow(d));
    return byId;
  }, [trades.data, deals.data]);   // eslint-disable-line react-hooks/exhaustive-deps

  // RECENT ACTIVITY = THE MOVEMENTS FEED PLUS THE DEALS IT DOES NOT CARRY.
  // A settle-later trade moves no account of the desk's, so the feed excludes
  // it by its own admission rule and Home would lose exactly the deals that
  // stay open — see withAbsentDeals. Deduplicated by deal, so a trade that did
  // move accounts arrives once, as a movement.
  //
  // THE MERGE NEEDS A FEED THAT IS CURRENT, NOT MERELY PRESENT (third Astra
  // review, 24 Sept 2026). A failed refetch leaves `data` holding the last good
  // rows while the query reports an error — the behaviour this card already
  // relies on to keep showing them. But "absent from the feed" is only a fact
  // about a deal while the feed is up to date: against a stale one, a spot
  // trade settled since that read looks absent, and would be drawn as agreed
  // and not yet moved, with no account and no balance — a settled trade
  // presented as an open position, on the screen where the operator looks for
  // open positions. So a stale feed still SHOWS its rows, which really
  // happened, but nothing is folded into it; the warning line says the money
  // side is behind.
  const feedRows    = money.data?.rows;
  const feedCurrent = !!feedRows && !money.isError;
  const activityRows = useMemo(
    () => (feedCurrent
      ? withAbsentDeals(feedRows, recentDeals, ACTIVITY_ROWS)
      : (feedRows ?? []).slice(0, ACTIVITY_ROWS)),
    [money.data, money.isError, deals.data],   // eslint-disable-line react-hooks/exhaustive-deps
  );
  // NO BALANCE COLUMN ON HOME (21 Oct 2026 audit): with no account filter the
  // Worker's `running` is the whole desk's balance in that currency, and beside
  // an Account column it read as that account's — "Office cash 2,314,870.25 AED"
  // above a Treasury card saying 1,735,039.25. Money movements keeps it, under a
  // header that says "closing" for the desk's total.

  // RECENT ACTIVITY IS TWO READS, AND A CARD THAT LOST ONE MUST SAY SO
  // (Astra review, 24 Sept 2026). While it was Recent money it stood on one
  // query and its states could read off that query alone; now a failed deals
  // read takes every settle-later trade off the card — the deals this desk
  // most needs to see — and a card that quietly showed fewer rows would be
  // worse than one that showed none. So: wait for both first reads; refuse
  // only when BOTH have nothing; otherwise show what did arrive and name what
  // did not, in the screens' own words (§6).
  //
  // THIS SCREEN ALREADY HAD THE RULE — see Needs attention below, where each
  // source has spoken for itself since 3 Sept 2026 ("both gates were AND, so a
  // quick empty cheque read plus a slow or failed settle sweep showed 'Nothing
  // needs attention' while trades were open"). Same fault, same answer; this
  // card is only being brought into line with it.
  // PENDING, not "loading": opened with no connection every first read is paused — no answer, nothing fetching, isLoading and
  // isError both false — and the card was drawing "No balances yet", "Nothing booked yet" and "Couldn't read … — null" for it
  // (Astra, 22 Oct 2026). A read with no answer and no failure is waiting.
  //
  // BUT ONE READ'S WAIT NEVER HIDES THE OTHER'S FAILURE OR ROWS (Astra, round 2b): the card waits as a whole only when BOTH reads
  // have not come. With one still pending, what the other said stands — its failure, its Try again, the rows in hand — and the
  // wait is said BESIDE them ("Reading trades…"). Each source's failure is drawn whatever the other is doing.
  const activityAllPending = money.isPending && deals.isPending;
  const activityBlind   = !money.data && !deals.data;
  const activityGap =
    money.isError && deals.isError ? 'trades and money'
    : money.isError ? 'money movements'
    : deals.isError ? 'trades'
    : null;
  // ONE LINE (design system, 22 Oct 2026): what could not be read, and that the rows below are the last good read; the
  // server's whole sentence is behind "Details", and "Try again" asks the two reads again.
  const readActivityAgain = () => { void money.refetch(); void deals.refetch(); };
  const activityWarning = activityGap && (
    <ReadFailed what={activityBlind ? 'recent activity' : activityGap} error={money.error ?? deals.error} says={activityRows.length > 0 ? 'showing the last good read' : undefined} retry={readActivityAgain} />
  );
  // The read that has not come (paused or still being made), beside what is shown — and never instead of it.
  const activityWaiting = !activityAllPending && (money.isPending || deals.isPending)
    ? <Pending what={`Reading ${money.isPending ? 'money movements' : 'trades'}…`} />
    : null;
  // "NOTHING BOOKED YET" IS A CLAIM, and only both reads together can make it
  // (second Astra review, 24 Sept 2026). With one of them failed the card has
  // no rows for a reason that is not "no activity", and the phone feed, which
  // hides itself when empty, would have taken the warning down with it — the
  // desk would have seen a quiet morning instead of a broken read.
  // …and an EMPTY feed is a claim that needs BOTH reads to have succeeded: not a failed one (the line says what failed), not one that has not come.
  const activityQuiet = activityRows.length === 0 && money.isSuccess && deals.isSuccess;

  if (me.isLoading) return <Pending />;
  // Web-context 401s never reach here (the api client clears the session and
  // redirects to /login) — this is the network/5xx path.
  if (me.isError) {
    return (
      <StateLine tone="bad">
        {isWebContext() ? 'Couldn’t read your operator profile — refresh, or sign in again.' : 'Open from inside Telegram.'}
      </StateLine>
    );
  }

  const today       = summary.data?.today;
  const volume      = collapseTotals(today?.volume_per_asset ?? {});
  const margin      = collapseTotals(today?.spread_per_asset ?? {});
  const volPrimary  = primaryEntry(volume);
  const mgnPrimary  = primaryEntry(margin);
  const pendingMcs  = mcs.data?.pending ?? [];
  const settleParties = settle.data?.parties ?? [];
  // Open items across the three sources — each unaccounted transfer counts as
  // one, as the Chain card's "N unaccounted for" did.
  const attentionCount = pendingMcs.length + settleParties.length + (chainQ.data?.counts.pending?.n ?? 0);
  const lastDeal    = recentDeals[0];
  // BOOKS DOWN IS ONE FACT, SAID ONCE (21 Oct 2026 audit). With Docker stopped —
  // it does not start by itself after a reboot — every read fails, and the page
  // said "The ledger did not answer. The books on the Mac may be down…" in six
  // cards, grey and calm. Three or more reads that failed with nothing to show
  // is the books being down: one amber banner with the way out, the strip's
  // dashes, and no card that could pass for an empty day. ONLY WHEN NOT ONE READ
  // HAS ANYTHING TO SHOW (Astra review, 21 Oct 2026: three failed reads beside
  // loaded cheques and settle-later rows hid that open work). A partial outage
  // keeps what loaded, and each failed card speaks for itself.
  const reads = [summary, deals, money, treasury, settle, mcs, chainQ];
  const failedReads = reads.filter((r) => r.isError && r.data === undefined);
  const booksDown = failedReads.length >= 3 && reads.every((r) => r.data === undefined);

  // The treasury glance.
  //
  // "Most relevant balances, not stale accounts" (operator, 2026-08-12): the
  // card takes tiers 0–1 only (pinned + live) in comparator order and shows
  // the top 5. `N more` is the TRUE count of every account not on screen —
  // including the stale ones — and the link lands on Treasury, which hides
  // nothing. The old rule (`balance !== 0`, endpoint order, 7 rows) both
  // showed husks with a stray 0.0001 and buried the funded accounts.
  const allRails    = treasury.data?.rows ?? [];
  // THE GLANCE SHOWS THE MOVING PARTS (operator, 2 Sept: "office cash,
  // aed/usd/inr/eur & hot wallets are the most moving parts. No need personal
  // banks."): cash in every currency and the hot wallets — the accounts a
  // trade touches — never banks on this card, however big their balance. The
  // full list is one tap away and hides nothing.
  const MOVING_KINDS = new Set(['cash', 'crypto', 'hot', 'wallet']);
  const movingRails  = allRails.filter((r) =>
    MOVING_KINDS.has((r.treasuryKind ?? r.slug?.split('.')[0] ?? '').toLowerCase()));
  const liveRails   = partitionByRelevance(movingRails, { maxTier: 1 }).shown;
  const glanceRails = liveRails.slice(0, GLANCE_RAILS);
  const moreRails   = allRails.length - glanceRails.length;

  // THE FOUR STRIP CELLS, once, for both layouts: the desktop band seats
  // Open cheques between Margin and Last trade while there is one; the
  // phone's 2×2 holds these four (its Open cheques are the section below
  // the tiles).
  const tradesCell = (
    <StripCell
      label="Trades today"
      value={today ? String(today.trade_count) : '—'}
    />
  );
  const volumeCell = (
    <StripCell
      label={volPrimary ? `Volume ${volPrimary[0]}` : 'Volume'}
      value={volPrimary ? fmtAmount(volPrimary[1], volPrimary[0]) : today ? '0' : '—'}
      parts={restParts(volume, volPrimary)}
    />
  );
  const marginCell = (
    <StripCell
      label={mgnPrimary ? `Margin ${mgnPrimary[0]}` : 'Margin'}
      value={mgnPrimary ? fmtAmount(mgnPrimary[1], mgnPrimary[0]) : today ? '0' : '—'}
      parts={restParts(margin, mgnPrimary)}
    />
  );
  // The amount is a part that is never cut; the party is the tail that may be (its whole name is in the journal below).
  const lastCell = (
    <StripCell
      label="Last trade"
      value={lastDeal ? fmtRelative(lastDeal.booked_at) : '—'}
      parts={lastDeal ? [`${fmtAmount(lastDeal.amount_take, lastDeal.asset_take)} ${displayAsset(lastDeal.asset_take)}`] : undefined}
      tail={lastDeal ? partyName({ display_name: lastDeal.party_name, slug: lastDeal.party_slug }) : undefined}
    />
  );

  return (
    <div className="space-y-4 pt-1">
      {booksDown && (
        <div role="alert">
          <Card tone="warn">
            <ReadFailed what="the books" error={failedReads[0]?.error} retry={() => { reads.forEach((r) => { void r.refetch(); }); }} />
          </Card>
        </div>
      )}

      {/* Terminal strip — desktop console band. Below lg nothing renders.
          A ZERO IS "0", A MISSING FIGURE IS "—" (7 Sept 2026): once the summary
          has landed, an empty per-asset map is a real nought for the day, not
          an unreadable figure. Always four cells: the Open cheques cell that
          came and went with the first cheque is Needs attention's now. */}
      <section className="hidden lg:grid grid-cols-4 gap-px rounded-xl overflow-hidden border border-stroke bg-stroke">
        {tradesCell}
        {volumeCell}
        {marginCell}
        {lastCell}
      </section>

      {/* Desktop home — NEEDS ATTENTION (two thirds) and Treasury (one third)
          on one row, then RECENT ACTIVITY across the whole width. Each card
          follows the empty-state contract: five words, at most one action.

          24 Sept 2026 — ONE JOURNAL WHERE TWO CARDS STOOD (operator, 21 Sept,
          on this screen: "we are having entries multiple times and repeating
          patterns bcoz trades cause money movements as well"; ruled "worth it
          — but not everywhere": the party page and Home, leaving Money
          movements and the Trades log alone). Recent trades listed a spot
          trade once and Recent money listed it again in each of its two
          currency blocks — one deal, three rows, which is what he was
          reading. MovementJournal stands every transaction ONCE, its
          currencies as lines inside its own row. The deal panel still opens
          from the Reference, and both screens keep their link. */}
      {!booksDown && (<>
      <section className="hidden lg:grid lg:grid-cols-3 gap-4 items-start">
        <div className="lg:col-span-2">
          <HomeCard
            title="Needs attention"
            action={attentionCount > 0
              ? <span className="font-mono tabular text-meta text-brand-muted">{attentionCount} open</span>
              : undefined}
          >
            <NeedsAttention mcs={mcs} settle={settle} chain={chainQ} />
          </HomeCard>
        </div>

        <HomeCard
          title="Wallets"
          action={<CardLink to="/treasury">All accounts →</CardLink>}
        >
          {treasury.isPending ? (
            <CardQuiet><Pending inline /></CardQuiet>
          ) : treasury.isError ? (
            <ReadFailed what="balances" error={treasury.error} retry={() => { void treasury.refetch(); }} className="px-4 py-3" />
          ) : glanceRails.length === 0 ? (
            allRails.length === 0
              ? <EmptyState action={<EmptyLink to="/record">Record money</EmptyLink>}>No balances yet.</EmptyState>
              : <EmptyState action={<EmptyLink to="/treasury">Wallets</EmptyLink>}>No moving accounts right now.</EmptyState>
          ) : (
            <ul className="divide-y divide-stroke-soft">
              {glanceRails.map((r) => <RailRow key={r.account} rail={r} />)}
              {moreRails > 0 && (
                <li>
                  <Link
                    to="/treasury"
                    className="block px-4 py-2 text-meta text-tg-hint transition-colors hover:text-tg-text"
                  >
                    More accounts ({moreRails}) →
                  </Link>
                </li>
              )}
            </ul>
          )}
        </HomeCard>
      </section>

      <section className="hidden lg:block">
        <HomeCard
          title="Recent activity"
          // §6 screen names: each link carries its screen's canonical name —
          // "All movements →" is a retired wording (check-screen-words).
          action={
            <span className="flex items-center gap-4">
              <CardLink to="/trades">All trades →</CardLink>
              <CardLink to="/movements">Money movements →</CardLink>
            </span>
          }
        >
          {activityAllPending ? (
            <CardQuiet><Pending inline /></CardQuiet>
          ) : activityQuiet ? (
            <EmptyState action={<EmptyLink to="/trade">Book trade</EmptyLink>}>Nothing booked yet.</EmptyState>
          ) : (
            // A failed focus refetch puts a query in error (TanStack v5) while `data` still holds the last good rows — those stay on
            // screen, with a line saying what is missing; a read that failed with nothing in hand is that line alone; a read that has
            // not come is said beside whatever the other one gave.
            <div className="overflow-x-auto px-2">
              {(activityWarning || activityWaiting) && <div className="space-y-1 px-2 py-2">{activityWarning}{activityWaiting}</div>}
              {activityRows.length > 0 && (
                <MovementJournal
                  rows={activityRows}
                  entity="dubaiotc"
                  showAccount
                  showBalance={false}
                  refs="deals"
                  dealsById={tradesById}
                  onOpenDeal={(id) => { const t = tradesById.get(id); if (t) setSelected(t); }}
                  compact
                />
              )}
            </div>
          )}
        </HomeCard>
      </section>

      </>)}

      {/* No action row at lg+ (20 Sept 2026): the rail pins the same four
          doors beside every screen, and a second copy under the grid was
          the clutter. The phone keeps its tiles below. */}

      {/* Mobile Home — hidden at lg+ where the home grid above replaces it. */}
      <div className="lg:hidden space-y-4">
        {/* The stat strip as a 2×2 card (19 Sept 2026) — the desktop band's
            four cells; StripCell's value is 18px below lg. */}
        <section className="grid grid-cols-2 gap-px rounded-xl overflow-hidden border border-stroke bg-stroke">
          {tradesCell}
          {volumeCell}
          {marginCell}
          {lastCell}
        </section>

        {/* The 4 core ops — uniform entry tiles. */}
        <ActionBar />

        {!booksDown && (<>
        {/* Needs attention — open cheques, settle-later parties and unaccounted
            transfers in ONE list, straight under the tiles (it stood last, under
            the journal, until 21 Oct 2026). It speaks for its own reads. */}
        <MobileFeed
          title={attentionCount > 0 ? `Needs attention · ${attentionCount}` : 'Needs attention'}
          isLoading={false}
          error={null}
          isEmpty={false}
        >
          <div className="card-tight">
            <NeedsAttention mcs={mcs} settle={settle} chain={chainQ} phone />
          </div>
        </MobileFeed>

        {/* Recent activity — the desktop card's journal on the phone's
            cards, one per transaction. The two feeds that stood here (Recent
            trades on DealRowLine, Recent money on MovementTable's cards)
            listed one spot trade three times between them; see the desktop
            section above for the operator's word. A failed focus refetch
            keeps the last good rows on screen with a line saying so, as the
            desktop card does. */}
        <MobileFeed
          title="Recent activity"
          isLoading={activityAllPending}
          error={null}
          isEmpty={activityQuiet}
        >
          {activityRows.length === 0 ? (
            // nothing in hand: the failure and/or the wait, alone
            <div className="space-y-1 px-1">{activityWarning}{activityWaiting}</div>
          ) : (
            <div className="card-tight">
              {(activityWarning || activityWaiting) && <div className="space-y-1 pt-1">{activityWarning}{activityWaiting}</div>}
              <div className="-mx-3">
                <MovementJournal
                  rows={activityRows}
                  entity="dubaiotc"
                  showAccount
                  showBalance={false}
                  refs="deals"
                  dealsById={tradesById}
                  onOpenDeal={(id) => { const t = tradesById.get(id); if (t) setSelected(t); }}
                  compact
                />
              </div>
            </div>
          )}
        </MobileFeed>

        {/* Treasury — the desktop card's five moving accounts. */}
        <MobileFeed
          title="Wallets"
          what="balances"
          isLoading={treasury.isPending}
          error={treasury.error}
          retry={() => { void treasury.refetch(); }}
          isEmpty={allRails.length === 0}
        >
          <div className="card-tight">
            {glanceRails.length === 0 ? (
              <EmptyState action={<EmptyLink to="/treasury">Wallets</EmptyLink>}>No moving accounts right now.</EmptyState>
            ) : (
              <ul className="divide-y divide-tg-secondary -mx-3">
                {glanceRails.map((r) => <RailRow key={r.account} rail={r} />)}
                {moreRails > 0 && (
                  <li>
                    <Link to="/treasury" className="block px-4 py-2 text-meta text-tg-hint transition-colors hover:text-tg-text">
                      More accounts ({moreRails}) →
                    </Link>
                  </li>
                )}
              </ul>
            )}
          </div>
        </MobileFeed>
        </>)}
      </div>

      {/* The trades log's own panel — one panel for both layouts. */}
      <DealSlideOver deal={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

interface StripCellProps {
  label: string;
  value: string;
  /** The second line, in parts — each one whole, never cut. The phone shows two and "+N"; the laptop three and "+N". */
  parts?: string[];
  /** A name after the parts: the one thing on the second line that may be cut short (an ellipsis), on a line of its own
   *  when it does not fit beside them. */
  tail?: string;
}

/** A figure longer than this does not fit the phone's cell in the title role at 360 px (13 characters is 140 of the cell's
 *  143; 123,456,789.25 is 14): it takes the next role down on the phone instead of being cut — a figure is never cut
 *  mid-number (§5). */
const FIGURE_FITS = 13;

function StripCell({ label, value, parts, tail }: StripCellProps) {
  const long = value.length > FIGURE_FITS;
  return (
    <div className="bg-tg-section-bg px-3 lg:px-4 pt-3 pb-2.5 min-w-0 transition-colors hover:bg-tg-secondary">
      <p className="text-label font-medium uppercase tracking-[0.09em] text-brand-muted">{label}</p>
      <p className={`amt-key ${long ? 'text-body' : 'text-title'} lg:text-figure leading-tight mt-1.5 truncate`}>{value}</p>
      <p className="font-mono tabular text-meta text-brand-muted mt-1 flex flex-wrap gap-x-1.5">
        {/* Each part is whole and carries its own dot at its end, so a line never starts with "·" and nothing is cut
            mid-figure; the parts may break BETWEEN each other. The phone has room for two and a count ("+2"), the laptop three. */}
        {parts && (
          <>
            <span className="contents lg:hidden">{partsFlow(parts, 2, !!tail)}</span>
            <span className="hidden lg:contents">{partsFlow(parts, 3, !!tail)}</span>
          </>
        )}
        {tail && <span className="min-w-0 truncate" title={tail}>{tail}</span>}
      </p>
    </div>
  );
}

/** A strip cell's second line as parts: each one unbreakable with its dot at its own end ("USDT 273.5K ·"), the dot kept
 *  when something follows (a count, or a `tail`); past `max` parts, "+N". */
function partsFlow(all: string[], max: number, tailFollows: boolean): ReactNode {
  const shown = all.slice(0, max);
  const more = all.length - shown.length;
  return (
    <>
      {shown.map((x, i) => (
        <span key={i} className="whitespace-nowrap">{x}{i < shown.length - 1 || more > 0 || tailFollows ? ' ·' : ''}</span>
      ))}
      {more > 0 && <span className="whitespace-nowrap">+{more}</span>}
    </>
  );
}

/** Lead asset for a per-asset totals map — AED if present, else largest by magnitude. */
function primaryEntry(totals: Record<string, number>): [string, number] | undefined {
  const entries = Object.entries(totals);
  if (entries.length === 0) return undefined;
  return entries.find(([k]) => k === 'AED')
    ?? [...entries].sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))[0];
}

function restParts(totals: Record<string, number>, primary?: [string, number]): string[] | undefined {
  const rest = Object.entries(totals).filter(([k]) => k !== primary?.[0]);
  if (rest.length === 0) return undefined;
  return rest.map(([k, v]) => `${k} ${fmtAmount(v, k)}`);   // rule 10: the figure, no K or M
}

// ── Desktop home grid (lg+) ──────────────────────────────────────────────────

function HomeCard({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-stroke bg-panel overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-stroke-soft">
        <h2 className="text-label font-medium uppercase tracking-[0.06em] text-tg-hint">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function CardLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="text-meta text-brand-muted hover:text-tg-accent transition-colors shrink-0">
      {children}
    </Link>
  );
}

/** A card that is still being read: one muted line (a failed read is never this — it is a ReadFailed). */
function CardQuiet({ children }: { children: ReactNode }) {
  return <p className="px-4 py-6 text-center text-meta text-brand-muted">{children}</p>;
}

/** The ONE action under an empty state (kit EmptyState: ≤ 6 words and one action): a link in the link colour, with its arrow. */
function EmptyLink({ to, children }: { to: string; children: string }) {
  return <Link to={to} className="text-tg-link transition-colors hover:text-tg-accent">{children} →</Link>;
}

/** One glance account. The raw `treasuryKind` enum chip that used to sit
 *  beside the name is gone: it printed an internal code ('hot', 'cold',
 *  'angadiya') at the operator, and the name already says which account. */
function RailRow({ rail }: { rail: TreasuryRow }) {
  const negative = rail.balance < 0;
  return (
    <li className="px-4 py-1">
      <Name
        primary={railName(rail)}
        trailing={
          <span className={`amt ${negative ? 'text-neg' : ''}`}>
            {fmtAmount(rail.balance, rail.asset)}
            <span className="text-meta text-brand-muted ml-1">{displayAsset(rail.asset)}</span>
          </span>
        }
      />
    </li>
  );
}

/** NEEDS ATTENTION — what the operator acts on, one list on both layouts. Each
 *  row says its own kind ("Pay seller · …", "Settle later · …", "Chain · In …"),
 *  so there are no headings, and the three reads are each answerable for
 *  themselves: a source that failed says so in its own amber line while the
 *  others' rows stay (3 Sept 2026: a quick empty cheque read plus a slow or
 *  failed settle sweep once showed "Nothing open" while trades were open — the
 *  one thing this list exists to prevent). Nothing open is said only when all
 *  three have answered and none failed. */
function NeedsAttention({ mcs, settle, chain, phone = false }: {
  mcs: UseQueryResult<Awaited<ReturnType<typeof api.pendingMcs>>>;
  settle: UseQueryResult<Awaited<ReturnType<typeof api.settlements>>>;
  chain: UseQueryResult<Awaited<ReturnType<typeof api.chainActivity>>>;
  phone?: boolean;
}) {
  const cheques = mcs.data?.pending ?? [];
  const parties = settle.data?.parties ?? [];
  const chainN  = chain.data?.counts.pending?.n ?? 0;
  const transfers = (chain.data?.rows ?? []).slice(0, CHAIN_GLANCE_ROWS);
  const failed = mcs.isError || settle.isError || chain.isError;
  // The padding of a line that is not a row: the desktop card's, or the phone card's own.
  const pad = phone ? 'px-1' : 'px-4';
  // A read still being made does not hold the others back: the rows that have
  // arrived stand, with a reading line for the rest (the chain read is the slow one).
  // UNRESOLVED = no answer and no error — loading, or PAUSED offline, which
  // isLoading does not say (Astra review, 21 Oct 2026: a paused read drew "Nothing open").
  const reading = [mcs, settle, chain].filter((r) => r.data === undefined && !r.isError).length;
  if (reading === 3) return <CardQuiet><Pending inline /></CardQuiet>;
  return (
    <>
      {/* A failed source is ONE amber line — what could not be read and the server's first sentence, the rest behind
          Details, and Try again — above the rows that did load (22 Oct 2026: two of these were six lines of amber
          above the work he had come for). */}
      {settle.isError && <ReadFailed what="settle-later trades" error={settle.error} retry={() => { void settle.refetch(); }} className={`${pad} py-2`} />}
      {mcs.isError && <ReadFailed what="open cheques" error={mcs.error} retry={() => { void mcs.refetch(); }} className={`${pad} py-2`} />}
      {chain.isError && <ReadFailed what="the chain" error={chain.error} retry={() => { void chain.refetch(); }} className={`${pad} py-2`} />}
      {!failed && reading === 0 && cheques.length === 0 && parties.length === 0 && chainN === 0 && (
        <EmptyState className="!py-3">Nothing open.</EmptyState>
      )}
      {(cheques.length > 0 || parties.length > 0 || transfers.length > 0) && (
        <div className={`divide-y ${phone ? 'divide-tg-secondary -mx-3' : 'divide-stroke-soft'}`}>
          {cheques.map((mc) => <AttentionRow key={mc.deal_id} mc={mc} />)}
          {parties.slice(0, 4).map((p) => <SettleRow key={p.slug} p={p} />)}
          {parties.length > 4 && (
            <Link to="/settle" className="block px-4 py-2 text-meta text-tg-hint transition-colors hover:text-tg-text">
              {parties.length - 4} more awaiting →
            </Link>
          )}
          {/* ONE CLICK TO THE ROW (operator, 14 Sept 2026: "chain tx not
              clickable from the dashboard — 1 additional unnecessary click
              every time"): each transfer opens the Chain screen with that row
              expanded and scrolled into view (?row=), where its Book trade /
              Record money / Cheque deposit links are. */}
          {transfers.map((r) => <ChainAttentionRow key={r.id} r={r} />)}
          {chainN > transfers.length && (
            <Link to="/chain" className="block px-4 py-2 text-meta text-tg-hint transition-colors hover:text-tg-text">
              {chainN - transfers.length} more unaccounted for →
            </Link>
          )}
        </div>
      )}
      {reading > 0 && <p className={`${pad} py-2`}><Pending inline /></p>}
    </>
  );
}

/** An on-chain transfer the books have no entry for: In / Out with the sign
 *  (§6 "Money direction" — the tone sits beside the word), its age at the end. */
function ChainAttentionRow({ r }: { r: ChainTransfer }) {
  const inbound = r.direction === 'in';
  return (
    <Link
      to={`/chain?row=${encodeURIComponent(String(r.id))}`}
      className="flex gap-3 px-4 py-2.5 hover:bg-tg-secondary transition-colors"
    >
      <span className="w-[3px] rounded-full self-stretch shrink-0 bg-warn" />
      <span className="flex min-w-0 flex-1 items-baseline gap-2">
        <span className="min-w-0 flex-1 line-clamp-2 break-words text-body font-medium lg:line-clamp-1">
          Chain · <span className={inbound ? 'text-pos' : 'text-neg'}>{inbound ? 'In' : 'Out'}</span>{' '}
          <span className="whitespace-nowrap">
            <span className="amt">{inbound ? '+' : '−'}{fmtAmount(r.amount, r.asset)}</span>{' '}
            <span className="font-normal text-tg-hint">{displayAsset(r.asset)}</span>
          </span>
        </span>
        <span className="shrink-0 font-mono tabular text-meta text-tg-hint">{fmtRelative(r.block_time)}</span>
      </span>
    </Link>
  );
}

function AttentionRow({ mc }: { mc: PendingMc }) {
  const paySeller = mc.lifecycle === 'mc_pending_seller';
  return (
    <Link
      to={`/mc/${encodeURIComponent(mc.deal_id)}`}
      className="flex gap-3 px-4 py-2.5 hover:bg-tg-secondary transition-colors"
    >
      <span
        className={`w-[3px] rounded-full self-stretch shrink-0 ${paySeller ? 'bg-neg' : 'bg-warn'}`}
      />
      <span className="min-w-0">
        {/* The name is what he reads first: two lines on the phone before a letter is cut, one from lg. */}
        <span className="line-clamp-2 break-words text-body font-medium lg:line-clamp-1">
          {paySeller ? 'Pay seller' : 'Deliver'} · {partyName({ display_name: mc.customer_name, slug: mc.customer_slug })}
        </span>
        <span className="mt-0.5 line-clamp-2 break-words text-meta text-tg-hint lg:line-clamp-1">
          <span className="whitespace-nowrap">Face <span className="amt">{fmtAmount(mc.mc_face_amount, 'AED')}</span> AED</span>
          {' '}· {partyName({ slug: mc.mc_seller_slug })} · booked {fmtRelative(mc.booked_at)}
        </span>
      </span>
    </Link>
  );
}

/** A line of facts, each one an unbreakable group: it breaks BETWEEN facts, at the dot, and never inside one — an amount stays
 *  with its currency. */
function Facts({ facts }: { facts: string[] }) {
  return <>{facts.map((f, i) => <Fragment key={i}>{i > 0 && ' · '}<span className="whitespace-nowrap">{f}</span></Fragment>)}</>;
}

/** A party with settle-later trades still open — the box in desk words. */
function SettleRow({ p }: { p: SettlementParty }) {
  return (
    <Link to="/settle" className="flex gap-3 px-4 py-2.5 hover:bg-tg-secondary transition-colors">
      <span className="w-[3px] rounded-full self-stretch shrink-0 bg-warn" />
      <span className="min-w-0">
        <span className="line-clamp-2 break-words text-body font-medium lg:line-clamp-1">
          Settle later · {partyName({ display_name: p.display_name, slug: p.slug })}
        </span>
        {/* The age leads, and the line is NEVER cut: what the party owes and what the desk holds are facts that stop a
            wrong click — a figure with its unit lost to a clamp ("you hold 120,000,000") is worse than a third line. */}
        {/* …and each fact is ONE group ("you hold 120,000,000 USDT"): the line breaks between facts, at the dot, never between an
            amount and its currency (Astra, 22 Oct 2026: "you hold 120,000,000" / "USDT"). */}
        <span className="mt-0.5 block break-words text-meta text-tg-hint">
          <Facts facts={[`oldest ${fmtRelative(p.oldest)}`, `${p.deals.length} trade${p.deals.length > 1 ? 's' : ''}`, ...partyBoxWords({ balances: p.box }).split(' · ')]} />
        </span>
      </span>
    </Link>
  );
}

// ── Mobile Home (below lg) ───────────────────────────────────────────────────

/** One feed on the phone's Home.
 *
 *  A FAILED READ IS NOT A QUIET DAY. The feeds used to render only when they
 *  already had rows, so a dead tunnel or a slow read left the phone showing the
 *  action tiles and nothing else — indistinguishable from a morning with no
 *  trades yet, which is the one reading the operator must never be given by
 *  accident. Loading and failure now say so in words; an empty SUCCESSFUL read
 *  still renders nothing, because that genuinely is a quiet day. */
function MobileFeed({ title, what, isLoading, error, retry, isEmpty, children }: {
  title: string;
  /** What could not be read, for the failed line ("recent activity"); the title in lower case when not given. */
  what?: string;
  isLoading: boolean;
  error: unknown;
  retry?: () => void;
  isEmpty: boolean;
  children: ReactNode;
}) {
  if (!isLoading && !error && isEmpty) return null;
  return (
    <section>
      <Heading className="mb-1.5 px-1">{title}</Heading>
      {isLoading ? <div className="px-1"><Pending /></div>
        : error ? <ReadFailed what={what ?? title.toLowerCase()} error={error} retry={retry} className="px-1" />
        : children}
    </section>
  );
}
